/*
 * ds-blog-carousel — arrows, dots, autoplay and centre-lift for
 * sections/ds-blog-carousel.liquid.
 *
 * Custom element, so it re-initialises on shopify:section:load and tears its
 * listeners down in disconnectedCallback. Enhancement only: with this file
 * absent the track is still a CSS scroll-snap carousel you can swipe, every
 * card is reachable, and the arrows/dots simply do nothing.
 *
 * Distinct from assets/blog-posts-list.js, which drives the theme's own stock
 * "Blog posts" section.
 */
/* Guarded IIFE: the section emits this <script> per instance, so the file
 * can be evaluated more than once on a page. A bare top-level `class`
 * declaration throws "Identifier has already been declared" on the second
 * pass — the define() guard alone is too late to help. */
(function () {
  if (customElements.get('ds-blog-carousel')) return;

class DsBlogCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-dsbc-track]');
    if (!this.track) return;

    this.cards = Array.from(this.querySelectorAll('[data-dsbc-card]'));
    if (!this.cards.length) return;

    this.dots = Array.from(this.querySelectorAll('[data-dsbc-dot]'));
    this.prevBtn = this.querySelector('[data-dsbc-prev]');
    this.nextBtn = this.querySelector('[data-dsbc-next]');

    this.index = 0;
    this.autoplayMs = (parseFloat(this.dataset.autoplay) || 0) * 1000;
    this.loop = this.dataset.loop === 'true';
    this.timer = null;

    this.onScroll = this.onScroll.bind(this);
    this.onKey = this.onKey.bind(this);
    this.pause = this.stopAutoplay.bind(this);
    this.resume = this.startAutoplay.bind(this);

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i, true));
    });

    // rAF-throttled: the scroll handler only reads geometry, never writes, so
    // it can never fight the browser's own smooth-scroll animation.
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKey);

    this.watchVisibility();
    this.bindAutoplayPauses();
    this.startAutoplay();
    this.sync();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.io) this.io.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('keydown', this.onKey);
    }
    this.removeEventListener('pointerenter', this.pause);
    this.removeEventListener('pointerleave', this.resume);
    this.removeEventListener('focusin', this.pause);
  }

  /* ---------- position tracking ---------- */

  onScroll() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      this.index = this.nearestIndex();
      this.sync();
    });
  }

  /*
   * Geometry is read from getBoundingClientRect, which is always physical
   * (left/right), never logical. scrollLeft is the opposite: in RTL some
   * engines count it up from zero and others down into negatives. Measuring
   * rects and moving with scrollBy keeps this correct in both directions
   * without branching on writing-direction anywhere.
   */
  nearestIndex() {
    // The last card is `scroll-snap-align: end`, so at maximum scroll its
    // start edge never lines up with the track's. Special-case it, or the
    // dots would stick on the second-to-last card forever.
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll > 0 && Math.abs(this.track.scrollLeft) >= maxScroll - 2) {
      return this.cards.length - 1;
    }

    const trackRect = this.track.getBoundingClientRect();
    const anchor = this.isRtl() ? trackRect.right : trackRect.left;
    let best = 0;
    let bestDist = Infinity;

    this.cards.forEach((card, i) => {
      const rect = card.getBoundingClientRect();
      const edge = this.isRtl() ? rect.right : rect.left;
      const dist = Math.abs(edge - anchor);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });

    return best;
  }

  isRtl() {
    return (this.getAttribute('dir') || document.documentElement.getAttribute('dir')) === 'rtl';
  }

  /* ---------- navigation ---------- */

  step(delta) {
    let target = this.index + delta;

    if (target < 0) target = this.loop ? this.cards.length - 1 : 0;
    else if (target > this.cards.length - 1) target = this.loop ? 0 : this.cards.length - 1;

    this.goTo(target, true);
  }

  goTo(i, isUserStep) {
    const card = this.cards[i];
    if (!card) return;

    const trackRect = this.track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    // Physical delta, so one call behaves identically in LTR and RTL.
    const delta = this.isRtl()
      ? cardRect.right - trackRect.right
      : cardRect.left - trackRect.left;

    this.track.scrollBy({ left: delta, behavior: 'smooth' });
    this.index = i;
    this.sync();

    // A deliberate tap should not be immediately overridden by autoplay.
    if (isUserStep) this.restartAutoplay();
  }

  onKey(e) {
    if (e.key === 'ArrowRight') this.step(this.isRtl() ? -1 : 1);
    else if (e.key === 'ArrowLeft') this.step(this.isRtl() ? 1 : -1);
    else return;
    e.preventDefault();
  }

  /* ---------- state reflection ---------- */

  sync() {
    this.dots.forEach((dot, i) => {
      const on = i === this.index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-current', on ? 'true' : 'false');
    });

    // Centre lift: only the card sitting in the middle of the viewport is
    // raised, matching the reference layout. Skipped entirely when the
    // merchant turned the effect off.
    if (this.dataset.centerLift === 'true') {
      const trackRect = this.track.getBoundingClientRect();
      const mid = trackRect.left + trackRect.width / 2;
      let best = null;
      let bestDist = Infinity;

      this.cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const dist = Math.abs(rect.left + rect.width / 2 - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = card;
        }
      });

      this.cards.forEach((card) => card.classList.toggle('is-center', card === best));
    }

    if (!this.loop) {
      if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index >= this.cards.length - 1;
    }
  }

  /* ---------- autoplay ---------- */

  bindAutoplayPauses() {
    // Pause while the visitor is reading, hovering or tabbing through.
    this.addEventListener('pointerenter', this.pause);
    this.addEventListener('pointerleave', this.resume);
    this.addEventListener('focusin', this.pause);
  }

  startAutoplay() {
    if (!this.autoplayMs) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.stopAutoplay();
    this.timer = setInterval(() => {
      // Without loop, stop at the last card instead of snapping back.
      if (!this.loop && this.index >= this.cards.length - 1) return this.stopAutoplay();
      this.step(1);
    }, this.autoplayMs);
  }

  restartAutoplay() {
    if (!this.autoplayMs) return;
    this.stopAutoplay();
    this.startAutoplay();
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /*
   * ds-section.liquid styles every [data-ds-anim] as opacity:0 and expects a
   * .ds-in class to reveal it. assets/ds-reveal.js only walks <ds-reveal>
   * hosts, so this element reveals its own children — otherwise the whole
   * carousel would sit permanently invisible.
   */
  watchVisibility() {
    const targets = this.querySelectorAll('[data-ds-anim]');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('ds-in'));
      return;
    }

    this.io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('ds-in');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => this.io.observe(el));
  }
}

customElements.define('ds-blog-carousel', DsBlogCarousel);
})();
